import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import DetailsAndStatus from '@features/campaigns/details/components/CampaignDetails/DetailsAndStatus/DetailsAndStatus';
import AvailabilityAndCost from '@features/campaigns/details/components/CampaignDetails/ImpressionsCalculator/AvailabilityAndCost';
import { CampaignDetailsViewData } from '@features/campaigns/details/containers/CampaignDetailsPage/CampaignDetailsPage.dto.ts';
import { AdProducts } from '@features/campaigns/shared/containers/Review/AdProducts/AdProducts';
import { CampaignDetails as CampaignDetailsSection } from '@features/campaigns/shared/containers/Review/CampaignDetails/CampaignDetails';
import { CampaignDetailsExternal } from '@features/campaigns/shared/containers/Review/CampaignDetailsExternal/CampaignDetailsExternal.tsx';
import { MediaType } from '@features/campaigns/shared/containers/Review/MediaType/MediaType';
import { Note } from '@features/campaigns/shared/containers/Review/Note/Note';
import { Target } from '@features/campaigns/shared/containers/Review/Target/Target';
import { CampaignTypes, InvalidSectionsItem } from '@features/campaigns/shared/types/campaign.ts';
import { WarningMessage } from '@shared/components/Toast/WarningMessage/WarningMessage.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { PossibleOnboardingState } from '@shared/types/userProfile.types.ts';

interface CampaignDetailsProps {
  data: CampaignDetailsViewData;
  invalidSections: InvalidSectionsItem[];
  onboardingState?: PossibleOnboardingState;
  isCurrentUserExternal: boolean;
  isOnboardingCompleted: boolean;
}

function CampaignDetails({
  data,
  invalidSections,
  onboardingState,
  isCurrentUserExternal,
  isOnboardingCompleted,
}: CampaignDetailsProps) {
  const { t } = useTranslation(i18nNameSpace.API);
  const shouldRenderCampaignDetailsExternalSection = data.type === CampaignTypes.SELF_SERVICE || isCurrentUserExternal;

  useEffect(() => {
    if (invalidSections.length === 0) {
      return;
    }

    invalidSections.forEach((section) => {
      toast.warning(<WarningMessage message={t(section.details)} />, {
        toastId: `campaign-details-invalid-section-${section.section}-${section.details}`,
      });
    });
  }, [invalidSections, t]);

  return (
    <section className='grid'>
      <div className='row'>
        <div className='col-4'>
          <DetailsAndStatus
            data={data.detailsAndStatus}
            formData={data.billingDetails}
            accessRights={data.accessRights}
            isOnboardingCompleted={isOnboardingCompleted}
          />
        </div>
        <div className='col-8'>
          <AvailabilityAndCost data={data.availabilityAndCost} />
          <MediaType headerText={'review.sectionTitle.mediaType'} data={data.mediaType} />
          {shouldRenderCampaignDetailsExternalSection ? (
            <CampaignDetailsExternal
              headerText={'review.sectionTitle.campaignDetails'}
              data={data.campaignDetails}
              accessRights={data.accessRights}
              onboardingState={onboardingState}
              campaignId={data.campaignId}
            />
          ) : (
            <CampaignDetailsSection
              headerText={'review.sectionTitle.campaignDetails'}
              data={data.campaignDetails}
              accessRights={data.accessRights}
            />
          )}

          <Target
            headerText={'review.sectionTitle.target'}
            data={data.target}
            campaignType={data.type}
            mediaType={data.mediaType}
          />
          <AdProducts
            headerText={'review.sectionTitle.adProducts'}
            data={data.adProducts}
            campaignType={data.type}
            campaignStatus={data.campaignStatus}
            mediaType={data.mediaType}
            campaignId={data.campaignId}
            isCurrentUserExternal={isCurrentUserExternal}
            accessRights={data.accessRights}
          />
          <Note headerText={'review.sectionTitle.additionalNote'} data={data.additionalNote} />
        </div>
      </div>
    </section>
  );
}

export default CampaignDetails;
